import * as React from "react";

import {AllStates, Level, StepForm, TranslateFunction} from "../Forms";

import {Checkbox, FormControlLabel, FormGroup, Link, Paper, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {getEnglishLevel, getGermanLevel, LanguageLevels} from "../languages/LanguagesForm";
import {activeApplicantModuleMappingsByProgram, getApplicableRequirementsDefinition} from "../mappings/MappingsPage";
import {Degrees, getDegreeProgramsArray, getProgramTitle} from "../degrees/Degrees";
import {Base} from "../base/Base";
import {Languages, LanguagesEnglish, LanguagesGerman} from "../languages/Languages";
import {FinalProject} from "../thesis/Thesis";
import {Comment} from "../comment/Comment";

import enCountries from "../i18n/countries/en.json";
import deCountries from "../i18n/countries/en.json";

import {TolgeeInstance, useTolgee, useTranslate} from "@tolgee/react";
import {download, pdfFormDocumentDefinition} from "./GeneratePdf";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import {I18nMessage, translate} from "../Representation";
import Button from "@mui/material/Button";
import {getFinalGradeEligibilityInfo} from "../degrees/DegreesForm";
import {getSubjectSpecificEligibilityInfo} from "../mappings/MappingsForm";
import {getFinalThesisEligibilityInfo} from "../thesis/ThesisForm";

const MISSING = "<missing>";

export type Row = {
    header?: string,
    subheader?: string,
    text?: string,
    item1?: { label: string, text: string, url?: boolean },
    item2?: { label: string, text: string, url?: boolean },
    module?: { title: string, credits: number, url?: string, proportions: { title: string, credits: number }[] }
};

export type Summary = {
    summaryData: {
        base: Base,
        languages: Languages,
        degrees: Degrees,
        finalProject: FinalProject,
        comment: Comment,
        completionId: string,
        moduleMappings: {
            programTitle: string;
            applicantModules: {
                title: string;
                credits: number;
                url: string | undefined;
                proportions: { requiredModuleKey: string; applicantCredits: number }[]
            }[]
        }[]
    },
    controlSheet: Row[]
};

export function summary(allStates: AllStates, t: TranslateFunction, lang: string): Summary {
    const {
        base: [base, _setBase],
        languages: [languages, _setLanguages],
        degrees: [degrees, _setDegrees],
        finalProject: [finalProject, _setFinalProject],
        comment: [comment, _setComment],
        startTime: [startTime, _setStartTime]} = allStates;

    const countries = lang === "de" ? deCountries : enCountries;

    const completionId = startTime?.toString() + "x" + Date.now();

    const applicantModulesByProgram = activeApplicantModuleMappingsByProgram(allStates);

    const programs = getDegreeProgramsArray(degrees);

    const prunedLanguages = {...languages};

    let keyEnglish: keyof LanguagesEnglish;
    for (keyEnglish in prunedLanguages.english) {
        if (keyEnglish !== "certificate" && keyEnglish !== prunedLanguages.english.certificate) {
            delete prunedLanguages.english[keyEnglish]; // no compiler error
        }
    }

    let keyGerman: keyof LanguagesGerman;
    for (keyGerman in prunedLanguages.german) {
        if (keyGerman !== "certificate" && keyGerman !== prunedLanguages.german.certificate) {
            delete prunedLanguages.german[keyGerman]; // no compiler error
        }
    }

    const data = {
        base: base,
        languages: languages,
        degrees: degrees,
        finalProject: finalProject,
        comment: comment,
        completionId: completionId,
        moduleMappings: applicantModulesByProgram.map((applicantModules, programIndex) => ({
                programTitle: getProgramTitle(programs[programIndex]),
                applicantModules: applicantModules.map(applicantModule => ({
                        title: applicantModule.title,
                        credits: applicantModule.credits,
                        url: applicantModule.url,
                        proportions: applicantModule.proportions.map(proportion => ({
                            requiredModuleKey: proportion.required_module_key,
                            applicantCredits: proportion.applicant_credits
                        })),
                    }
                ))
            }
        ))
    }

    const requirements = getApplicableRequirementsDefinition(allStates);
    const requiredModuleKeyToTitle = new Map<string, string>();
    requirements.areas.flatMap(area => area.modules).forEach(module => {
        requiredModuleKeyToTitle.set(module.key, lang === "de" ? module.title.de : module.title.en);
    });

    const controlsheet: Row[] =
        [
            {header: t("base_title")},
            {
                item1: {
                    label: t("base_form.personal_data.given_name.label"),
                    text: base.personal_data.given_name ?? MISSING
                },
                item2: {label: t("base_form.personal_data.surname.label"), text: base.personal_data.surname ?? MISSING}
            },
            {
                item1: {
                    label: t("base_form.personal_data.birthday.label"),
                    text: base.personal_data.birthday ?? MISSING
                },
                item2: {
                    label: t("base_form.personal_data.birthplace.label"),
                    text: base.personal_data.birthplace ?? MISSING
                }
            },
            {item1: {label: t("base_form.study_program.label"), text: "MSc " + base.study_program.subject ?? MISSING}},
            {item1: {label: t("completion_id"), text: completionId}},
            {header: t("languages_title")},
        ];

    const englishCEFR = getEnglishLevel(languages);

    controlsheet.push({
        item1: {
            label: t("languages_form.english.certificate.label"),
            text: languages.english.certificate ?
                t("languages_form.english.certificate." + languages.english.certificate, MISSING) :
                MISSING
        },
        item2: {
            label: t("cefr_level"),
            text: englishCEFR === LanguageLevels.INSUFFICIENT ? "< B1" : ">= " + LanguageLevels[englishCEFR]
        }
    });

    switch (languages.english.certificate) {
        case "ielts": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.ielts.result.label"),
                    text: languages.english.ielts?.result ?? MISSING
                }
            });
            break;
        }
        case "ibt": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.ibt.points.label"),
                    text: languages.english.ibt?.points?.toString() ?? MISSING
                }
            });
            break;
        }
        case "pbt": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.pbt.points.label"),
                    text: languages.english.pbt?.points?.toString() ?? MISSING
                }
            });
            break;
        }
        case "itp": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.itp.points.label"),
                    text: languages.english.itp?.points?.toString() ?? MISSING
                }
            });
            break;
        }
        case "fce": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.fce.grade.label"),
                    text: languages.english.fce?.grade ?? MISSING
                }
            });
            break;
        }
        case "cae": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.cae.grade.label"),
                    text: languages.english.cae?.grade ?? MISSING
                }
            });
            break;
        }
        case "bec_higher": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.bec_higher.grade.label"),
                    text: languages.english.bec_higher?.grade ?? MISSING
                }
            });
            break;
        }
        case "pet": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.pet.grade.label"),
                    text: languages.english.pet?.grade ?? MISSING
                }
            });
            break;
        }
        case "bec_preliminary": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.bec_preliminary.grade.label"),
                    text: languages.english.bec_preliminary?.grade ?? MISSING
                }
            });
            break;
        }
        case "pearson": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.pearson.points.label"),
                    text: languages.english.pearson?.points?.toString() ?? MISSING
                }
            });
            break;
        }
        case "toeic": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.toeic.listening and reading.label"),
                    text: languages.english.toeic?.["listening and reading"]?.toString() ?? MISSING
                },
                item2: {
                    label: t("languages_form.english.toeic.speaking.label"),
                    text: languages.english.toeic?.["speaking"]?.toString() ?? MISSING
                }
            });
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.toeic.writing.label"),
                    text: languages.english.toeic?.["writing"]?.toString() ?? MISSING
                }
            });
            break;
        }
        case "toeic_bridge": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.toeic_bridge.points.label"),
                    text: languages.english.toeic_bridge?.points?.toString() ?? MISSING
                }
            });
            break;
        }
        case "telc": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.telc.level.label"),
                    text: languages.english.telc?.level ?? MISSING
                }
            });
            break;
        }
        case "unicert": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.unicert.level.label"),
                    text: languages.english.unicert?.level ?? MISSING
                }
            });
            break;
        }
        case "igcse": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.igcse.listening and speaking.label"),
                    text: languages.english.igcse?.["listening and speaking"] ?? MISSING
                },
                item2: {
                    label: t("languages_form.english.igcse.reading.label"),
                    text: languages.english.igcse?.["reading"] ?? MISSING
                }
            });
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.igcse.writing.label"),
                    text: languages.english.igcse?.["writing"] ?? MISSING
                }
            });
            break;
        }
        case "abitur": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.abitur.b1.label"),
                    text: languages.english.abitur?.b1 ? "B1" : "< B1"
                }
            });
            break;
        }
        case "other": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.english.other.b1.label"),
                    text: languages.english.other?.b1 ? "B1" : "< B1"
                }
            });
            break;
        }
    }

    const germanCEFR = getGermanLevel(languages);

    controlsheet.push({
        item1: {
            label: t("languages_form.german.certificate.label"),
            text: languages.english.certificate ?
                t("languages_form.german.certificate." + languages.german.certificate, MISSING) :
                MISSING
        },
        item2: {
            label: t("cefr_level"),
            text: germanCEFR === LanguageLevels.INSUFFICIENT ? "< B1" : ">= " + LanguageLevels[germanCEFR]
        }
    });

    switch (languages.german.certificate) {
        case "dsh": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.dsh.level.label"),
                    text: languages.german.dsh?.level ?? MISSING
                }
            });
            break;
        }
        case "testdaf": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.testdaf.reading.label"),
                    text: languages.german.testdaf?.reading ?? MISSING
                },
                item2: {
                    label: t("languages_form.german.testdaf.written.label"),
                    text: languages.german.testdaf?.written ?? MISSING
                }
            });
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.testdaf.listening.label"),
                    text: languages.german.testdaf?.listening ?? MISSING
                },
                item2: {
                    label: t("languages_form.german.testdaf.oral.label"),
                    text: languages.german.testdaf?.oral ?? MISSING
                }
            });
            break;
        }
        case "studienkolleg": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.studienkolleg.passed.label"),
                    text: languages.german.studienkolleg?.passed ? t("yes") : t("no")
                }
            });
            break;
        }
        case "dsdii": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.dsdii.reading.label"),
                    text: languages.german.dsdii?.reading ?? MISSING
                },
                item2: {
                    label: t("languages_form.german.dsdii.written.label"),
                    text: languages.german.dsdii?.written ?? MISSING
                }
            });
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.dsdii.listening.label"),
                    text: languages.german.dsdii?.listening ?? MISSING
                },
                item2: {
                    label: t("languages_form.german.dsdii.oral.label"),
                    text: languages.german.dsdii?.oral ?? MISSING
                }
            });
            break;
        }
        case "gds": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.gds.passed.label"),
                    text: languages.german.gds?.passed ? t("yes") : t("no")
                }
            });
            break;
        }
        case "osd": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.osd.level.label"),
                    text: languages.german.osd?.level ?? MISSING
                }
            });
            break;
        }
        case "telc": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.telc.passed.label"),
                    text: languages.german.telc?.passed ? t("yes") : t("no")
                }
            });
            break;
        }
        case "schule": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.schule.equivalent.label"),
                    text: languages.german.schule?.equivalent ? t("yes") : t("no")
                }
            });
            break;
        }
        case "hochschule": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.hochschule.equivalent.label"),
                    text: languages.german.hochschule?.equivalent ? t("yes") : t("no")
                }
            });
            break;
        }
        case "zeugnisse": {
            controlsheet.push({
                item1: {
                    label: t("languages_form.german.zeugnisse.equivalent.label"),
                    text: languages.german.zeugnisse?.equivalent ? t("yes") : t("no")
                }
            });
            break;
        }
    }

    controlsheet.push(
        {header: t("degrees_form.qualifying_degree.label")},
        {
            item1: {
                label: t("degrees_form.qualifying_degree.university.label"),
                text: degrees.qualifying_degree.university ?? MISSING
            }
        },
        {
            item1: {
                label: t("degrees_form.qualifying_degree.country.label"),
                text: degrees.qualifying_degree.country ?
                    (countries as { [k: string]: string })[degrees.qualifying_degree.country] ?? MISSING :
                    MISSING
            },
            item2: {
                label: t("degrees_form.qualifying_degree.city.label"),
                text: degrees.qualifying_degree.city ?? MISSING
            }
        },
        {
            item1: {
                label: t("degrees_form.qualifying_degree.degree_level.label"),
                text: degrees.qualifying_degree.degree_level ?? MISSING
            },
            item2: {
                label: t("degrees_form.qualifying_degree.subject.label"),
                text: degrees.qualifying_degree.subject ?? MISSING
            }
        },
        {
            item1: {
                label: t("degrees_form.qualifying_degree.total_credits.label"),
                text: degrees.qualifying_degree.total_credits?.toString() ?? MISSING
            },
            item2: {
                label: t("almost_complete.label"),
                text: degrees.qualifying_degree.almost_complete ? t("yes") : t("no")
            }
        }
    );
    if (degrees.qualifying_degree.country !== "DE") {
        controlsheet.push({
            item1: {
                label: t("degrees_form.qualifying_degree.min.label"),
                text: degrees.qualifying_degree.min?.toString() ?? MISSING
            },
            item2: {
                label: t("degrees_form.qualifying_degree.max.label"),
                text: degrees.qualifying_degree.max?.toString() ?? MISSING
            }
        });
    }
    controlsheet.push({
        item1: {
            label: t("degrees_form.qualifying_degree.gpa.label"),
            text: degrees.qualifying_degree.gpa?.toString() ?? MISSING
        }
    });

    degrees.further_degree_programs.forEach(furtherDegree => {
            controlsheet.push(
                {header: t("degrees_form.further_degree_program.label")},
                {
                    item1: {
                        label: t("degrees_form.further_degree_programs.university.label"),
                        text: furtherDegree.university ?? MISSING
                    }
                },
                {
                    item1: {
                        label: t("degrees_form.further_degree_programs.country.label"),
                        text: furtherDegree.country ?
                            (countries as { [k: string]: string })[furtherDegree.country] ?? MISSING :
                            MISSING
                    },
                    item2: {
                        label: t("degrees_form.further_degree_programs.city.label"),
                        text: furtherDegree.city ?? MISSING
                    }
                },
                {
                    item1: {
                        label: t("degrees_form.further_degree_programs.degree_level.label"),
                        text: furtherDegree.degree_level ?? MISSING
                    },
                    item2: {
                        label: t("degrees_form.further_degree_programs.subject.label"),
                        text: furtherDegree.subject ?? MISSING
                    }
                },
                {
                    item1: {
                        label: t("degrees_form.further_degree_programs.total_credits.label"),
                        text: furtherDegree.total_credits?.toString() ?? MISSING
                    }
                }
            );
        }
    );

    controlsheet.push({header: t("mappings_title")})
    programs.forEach((program, programIndex) => {
        controlsheet.push({subheader: getProgramTitle(program)});
        applicantModulesByProgram[programIndex].forEach((moduleMapping) => {
            controlsheet.push({
                module: {
                    title: moduleMapping.title ?? MISSING,
                    credits: moduleMapping.credits ?? -1,
                    url: moduleMapping.url,
                    proportions: moduleMapping.proportions.filter(proportion => requiredModuleKeyToTitle.has(proportion.required_module_key)).map(proportion => ({
                        title: requiredModuleKeyToTitle.get(proportion.required_module_key) ?? MISSING,
                        credits: proportion.applicant_credits
                    })) ?? []
                }
            });
        });
    });

    controlsheet.push(
        {header: t("thesis_title")},
        {
            item1: {
                label: t("thesis_form.degree_of_obligation.label"),
                text: t("thesis_form.degree_of_obligation." + finalProject.degree_of_obligation, MISSING)
            }
        }
    );

    if (finalProject.degree_of_obligation !== "none") {
        controlsheet.push(
            {
                item1: {
                    label: t("thesis_form.status.label"),
                    text: t("thesis_form.status." + finalProject.status, MISSING)
                },
                item2: {label: t("thesis_form.scope.label"), text: finalProject.scope?.toString() ?? MISSING}
            },
            {item1: {label: t("thesis_form.tor.label"), text: finalProject.tor ?? MISSING}},
            {
                item1: {
                    label: t("thesis_form.references.label"),
                    text: finalProject.references?.toString() ?? MISSING,
                    url: true
                }
            },
            {
                item1: {
                    label: t("thesis_form.references_later.label"),
                    text: finalProject.references_later ? t("yes") : t("no")
                }
            }
        );
    }

    controlsheet.push(
        {header: t("comment_title")},
        {text: comment.comment ?? t("none", {noWrap: true})},
    );

    return {
        summaryData: data,
        controlSheet: controlsheet
    };

}

export function unwrap(summary: Summary, tolgee: TolgeeInstance): Summary {
    return {
        summaryData: summary.summaryData,
        controlSheet: summary.controlSheet.map(row => (
            {
                header: row.header ? tolgee.unwrap(row.header).text : undefined,
                subheader: row.subheader ? tolgee.unwrap(row.subheader).text : undefined,
                text: row.text ? tolgee.unwrap(row.text).text : undefined,
                item1: row.item1 ? {
                    label: tolgee.unwrap(row.item1.label).text,
                    text: tolgee.unwrap(row.item1.text).text,
                    url: row.item1.url
                } : undefined,
                item2: row.item2 ? {
                    label: tolgee.unwrap(row.item2.label).text,
                    text: tolgee.unwrap(row.item2.text).text,
                    url: row.item2.url
                } : undefined,
                module: row.module ? {
                    title: tolgee.unwrap(row.module.title).text,
                    credits: row.module.credits,
                    url: row.module.url,
                    proportions: row.module.proportions.map(proportion => ({
                        title: tolgee.unwrap(proportion.title).text,
                        credits: proportion.credits
                    }))
                } : undefined
            }))
    };
}

function htmlTable(controlsheet: Row[], t: TranslateFunction) {
    return <Grid container spacing={2}>
        {controlsheet.map((row, i) => {
            if (row.header || row.subheader) {
                return (
                    <Grid size={12} key={"row" + i}>
                        <Typography
                            variant={row.header ? "h4" : "h5"}>{row.header ? row.header : row.subheader}</Typography>
                    </Grid>);
            } else if (row.text) {
                return (
                    <Grid size={12} key={"row" + i}>
                        <Typography boxShadow={2} padding={1}>{row.text}</Typography>
                    </Grid>);
            } else if (row.item1) {
                return (
                    <React.Fragment key={"row" + i}>
                        <Grid size={2}>
                            <Typography>{row.item1?.label}:</Typography>
                        </Grid>
                        <Grid size={row.item2 ? 4 : 10}>
                            <Typography boxShadow={2} padding={1}>{row.item1.url ?
                                <Link target="_blank" href={row.item1.text}>{row.item1.text}</Link> : row.item1?.text}
                            </Typography>
                        </Grid>
                        {
                            row.item2 ? (
                                    <React.Fragment>
                                        <Grid size={2}>
                                            <Typography>{row.item2?.label}:</Typography>
                                        </Grid>
                                        <Grid size={4}>
                                            <Typography boxShadow={2} padding={1}>{row.item2.url ? <Link target="_blank"
                                                                                                         href={row.item2.text}>{row.item2.text}</Link> : row.item2?.text}</Typography>
                                        </Grid>
                                    </React.Fragment>
                                ) :
                                <React.Fragment> </React.Fragment>
                        }
                    </React.Fragment>);
            } else if (row.module) {
                return <Grid size={12} key={"row" + i}>
                    <Typography boxShadow={2} padding={1}>
                        {row.module.title + " (" + row.module.credits + " " + t("cp") + ")"}
                        {row.module.url ? <React.Fragment><br/><Link target="_blank"
                                                                     href={row.module.url}>{row.module.url}</Link></React.Fragment> :
                            <React.Fragment/>}
                        <br/>{t("mapped_to")}<br/>
                        {row.module.proportions.length > 0 ?
                            row.module.proportions.map(proportion => <React.Fragment
                                key={proportion.title}><span>{"- " + proportion.title + " (" + proportion.credits + " " + t("cp") + ")"}</span><br/></React.Fragment>) :
                            t("none")
                        }
                    </Typography>
                </Grid>;
            } else {
                return <React.Fragment key={"row" + i}></React.Fragment>;
            }
        })}
    </Grid>;
}

function FinishPage(props: {
    allStates: AllStates,
    allStepForms: StepForm[]
}) {
    const [accept, setAccept] = React.useState(false);

    const {t} = useTranslate();
    const tolgee = useTolgee();
    const lang = tolgee.getLanguage() ?? "en";

    const formSummary = summary(props.allStates, t, lang);

    const formSummaryPrint = {
        summaryData: {...formSummary.summaryData, accept: accept},
        controlSheet: [
            ...formSummary.controlSheet,
            {header: t("accept_title")},
            {text: accept ? t("accept") : t("do_not_accept")}
        ]
    }


    const downloadAction = () => {
        download(pdfFormDocumentDefinition(unwrap(formSummaryPrint, tolgee), t), "self-disclosure.pdf")
    }

    const errors: {stepTitleKey: string, messages: I18nMessage[]}[] = [];
    const warnings: {stepTitleKey: string, messages: I18nMessage[]}[] = [];
    for (let i = 0; i < props.allStepForms.length; i++) {
        const stepForm = props.allStepForms[i];
        const formData = props.allStates.ordered[i][0];
        if (!props.allStepForms[i].isValid(formData, props.allStates, t)) {
            errors.push(
                {
                    stepTitleKey: stepForm.getTitleKey(),
                    messages: stepForm.getStatus(formData, props.allStates, false, t).messages
                });
        } else if (props.allStepForms[i].getStatus(formData, props.allStates, false, t).level !== Level.OK) {
            warnings.push(
                {
                    stepTitleKey: stepForm.getTitleKey(),
                    messages: stepForm.getStatus(formData, props.allStates, false, t).messages
                });
        }
    }

    const {eligibilityPoints: epFinalGrade} = getFinalGradeEligibilityInfo(props.allStates);
    const {eligibilityPoints: epSubjectSpecific} = getSubjectSpecificEligibilityInfo(props.allStates);
    const {eligibilityPoints: epFinalThesis} = getFinalThesisEligibilityInfo(props.allStates);

    return <React.Fragment>
        <Stack spacing={2}>
            {
                (errors.length > 0)
                    ? (
                        <React.Fragment key={"errors"}>
                            <Paper elevation={2}>
                                <Box>
                                    <Stack spacing={2} padding={2}>
                                        <Typography variant="h4" key={"header"}>
                                            {t("errors_present")}
                                        </Typography>
                                        <Typography key={"body"}>
                                            {t("fix_errors_before_submission")}
                                        </Typography>
                                        {
                                            errors.map((error, i) => (
                                                <React.Fragment key={"error_" + i}>
                                                    <Typography variant="h5" key={"header"}>{t(error.stepTitleKey)}</Typography>
                                                    <Typography component="ul" key={"body"}>
                                                        {
                                                            error.messages.map((message, i) => (
                                                                <Typography component="li" key={"message_" + i}>
                                                                    {translate(message, t)}
                                                                </Typography>
                                                            ))
                                                        }
                                                    </Typography>
                                                </React.Fragment>
                                            ))
                                        }
                                    </Stack>
                                </Box>
                            </Paper>
                        </React.Fragment>
                    )
                    : (
                        <React.Fragment key={"errors"}></React.Fragment>
                    )
            }
            {
                (warnings.length > 0)
                    ? (
                        <React.Fragment key={"warnings"}>
                            <Paper elevation={2}>
                                <Box>
                                    <Stack spacing={2} padding={2}>
                                        <Typography variant="h4" key={"header"}>
                                            {t("warnings_present")}
                                        </Typography>
                                        {
                                            warnings.map((warning, i) => (
                                                <React.Fragment key={"warning_" + i}>
                                                    <Typography variant="h5" key={"header"}>{t(warning.stepTitleKey)}</Typography>
                                                    <Typography component="ul" key={"body"}>
                                                        {
                                                            warning.messages.map((message, i) => (
                                                                <Typography component="li" key={"message_" + i}>
                                                                    {translate(message, t)}
                                                                </Typography>
                                                            ))
                                                        }
                                                    </Typography>
                                                </React.Fragment>
                                            ))
                                        }
                                    </Stack>
                                </Box>
                            </Paper>
                        </React.Fragment>
                    )
                    : (
                        <React.Fragment key={"warnings"}></React.Fragment>
                    )
            }
            {
                (errors.length === 0 && (epFinalGrade + epSubjectSpecific + epFinalThesis < 65))
                    ? (
                        <React.Fragment key={"eligibility-points"}>
                            <Paper elevation={2}>
                                <Box>
                                    <Stack spacing={2} padding={2}>
                                        <Typography variant="h4" key={"header"}>
                                            {t("eligibility_points_insufficient_header")}
                                        </Typography>
                                        <Typography key={"body"}>
                                            {t("eligibility_points_insufficient",
                                                "{epFinalGrade} {epSubjectSpecific} {epFinalThesis} {epTotal}",
                                                {epFinalGrade: epFinalGrade, epSubjectSpecific: epSubjectSpecific, epFinalThesis: epFinalThesis, epTotal: epFinalGrade + epSubjectSpecific + epFinalThesis})}

                                        </Typography>
                                    </Stack>
                                </Box>
                            </Paper>
                        </React.Fragment>
                    )
                    : (
                        <React.Fragment key={"eligibility-points"}></React.Fragment>
                    )
            }
            {
                htmlTable(formSummary.controlSheet, t)
            }
            {
                <React.Fragment key={"download"}>
                    <Stack spacing={2} padding={2}>
                        <FormGroup key="form-group">
                            <FormControlLabel
                                required
                                control={
                                    <Checkbox
                                        checked={accept}
                                        onChange={() => setAccept(!accept)}
                                    />
                                }
                                label={t("accept")}
                            />
                        </FormGroup>
                        <Button
                            key="button"
                            disabled={!accept}
                            onClick={downloadAction}
                            color="primary"
                            variant="contained"
                        >
                            {t("download")}
                        </Button>
                    </Stack>
                </React.Fragment>
            }
        </Stack>
    </React.Fragment>;
}

export default FinishPage;